import React from 'react';
import { SelectItem } from './ui/select';
import { sortUnitsForSelect } from '../lib/utils';

/**
 * UnitSelectItems — drop-in <SelectItem> list for any unit dropdown in the app.
 * Active units render first, then a subtle "Inactive" divider label, then
 * inactive units rendered grayed-out (but still selectable so historical
 * records can be filtered / viewed / exported).
 *
 * Usage:
 *   <SelectContent>
 *     <UnitSelectItems units={units} />
 *   </SelectContent>
 *
 * If you want an "All units" row on top, pass includeAll (label optional).
 * If you want to force-hide inactive units from the list (rare — used by
 * pickers that create NEW work only), pass hideInactive.
 */
export default function UnitSelectItems({
  units,
  includeAll = false,
  allLabel = 'All units',
  allValue = 'all',
  hideInactive = false,
  includeCompany = false,   // adds a "Company" row (pseudo-unit) at the top
  renderExtra = null,
}) {
  const sorted = sortUnitsForSelect(units);
  const active = sorted.filter(u => (u?.status || 'active') === 'active');
  const inactive = hideInactive ? [] : sorted.filter(u => (u?.status || 'active') !== 'active');

  return (
    <>
      {includeAll && (
        <SelectItem value={allValue} data-testid="unit-select-all">{allLabel}</SelectItem>
      )}
      {includeCompany && (
        <SelectItem value="COMPANY" data-testid="unit-select-company">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
            Company
            <span className="text-[10px] text-muted-foreground">(no unit)</span>
          </span>
        </SelectItem>
      )}
      {active.map(u => (
        <SelectItem key={u.id} value={u.id} data-testid={`unit-select-${u.unit_number}`}>
          {u.unit_number}{renderExtra ? renderExtra(u) : ''}
        </SelectItem>
      ))}
      {inactive.length > 0 && (
        <div
          className="px-2 py-1.5 mt-1 border-t text-[10px] font-semibold uppercase tracking-wider text-muted-foreground select-none"
          data-testid="unit-select-inactive-divider"
        >
          ── Inactive ──
        </div>
      )}
      {inactive.map(u => (
        <SelectItem
          key={u.id}
          value={u.id}
          className="opacity-60 italic"
          data-testid={`unit-select-inactive-${u.unit_number}`}
        >
          {u.unit_number}{renderExtra ? renderExtra(u) : ''} <span className="text-[10px] text-muted-foreground ml-1">(inactive)</span>
        </SelectItem>
      ))}
    </>
  );
}
